import { useState, useEffect } from 'react';
import { useApp, ROLES } from '../../context/AppContext';
import { StatusTracker } from '../../components/shared/StatusTracker';
import { StatusBadge } from '../../components/shared/StatusBadge';
import { STATUS_CONFIG, CASE_STATUSES } from '../../data/mockData';
import { fetchCases, submitCaseDeletion } from '../../api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  Search,
  GitBranch,
  ArrowRight,
  CheckCircle2,
  Clock,
  AlertTriangle,
  XCircle,
  Trash2,
  FileEdit,
} from 'lucide-react';
import { format } from 'date-fns';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';

const statusSteps = [
  { key: 'created', label: 'Created' },
  { key: 'assigned', label: 'Assigned' },
  { key: 'in_progress', label: 'In Progress' },
  { key: 'verified', label: 'Verified' },
  { key: 'qc', label: 'QC' },
  { key: 'completed', label: 'Completed' },
  { key: 'report_sent', label: 'Report Sent' },
];

const getStepFromStatus = (status) => {
  const statusStepMap = {
    [CASE_STATUSES.CREATED]: 0,
    [CASE_STATUSES.ASSIGNED]: 1,
    [CASE_STATUSES.IN_PROGRESS]: 2,
    [CASE_STATUSES.VERIFIED]: 3,
    [CASE_STATUSES.QC_PENDING]: 4,
    [CASE_STATUSES.QC_APPROVED]: 5,
    [CASE_STATUSES.QC_REJECTED]: 4,
    [CASE_STATUSES.REVISIT]: 2,
    [CASE_STATUSES.COMPLETED]: 5,
    [CASE_STATUSES.REPORT_SENT]: 6,
  };
  return statusStepMap[status] || 0;
};

export default function StatusTrackerPage() {
  const { currentRole } = useApp();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCase, setSelectedCase] = useState(null);
  const [cases, setCases] = useState([]);
  
  useEffect(() => {
    async function load() {
      try {
        const data = await fetchCases();
        if (Array.isArray(data)) setCases(data);
      } catch (err) {
        console.error("Failed to load cases in StatusTracker:", err);
        setCases([]);
      }
    }
    load();
  }, []);
  
  // Modals state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [editFormModalOpen, setEditFormModalOpen] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [editFormData, setEditFormData] = useState({
    applicantName: '',
    address: '',
    remarks: ''
  });

  const baseCases = currentRole === ROLES.VENDOR 
    ? cases.filter(c => c.assignedTo && ['vendor', 'field_executive'].includes(c.assignedTo.role)) 
    : currentRole === ROLES.CLIENT 
    ? cases.filter(c => c.client && c.client.name === 'HDFC Bank')
    : cases;

  const filteredCases = baseCases.filter(c =>
    c.refNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.applicantName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const statusCounts = {
    created: baseCases.filter(c => c.status === CASE_STATUSES.CREATED).length,
    inProgress: baseCases.filter(c => [CASE_STATUSES.ASSIGNED, CASE_STATUSES.IN_PROGRESS].includes(c.status)).length,
    qcPending: baseCases.filter(c => c.status === CASE_STATUSES.QC_PENDING).length,
    completed: baseCases.filter(c => [CASE_STATUSES.COMPLETED, CASE_STATUSES.REPORT_SENT].includes(c.status)).length,
    issues: baseCases.filter(c => [CASE_STATUSES.QC_REJECTED, CASE_STATUSES.REVISIT].includes(c.status)).length,
  };

  useEffect(() => {
    if (filteredCases.length > 0 && !selectedCase) {
      setSelectedCase(filteredCases[0]);
    }
  }, [filteredCases, selectedCase]);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="status-tracker-page">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Status Tracker</h1>
        <p className="text-sm text-slate-500 mt-1">Visual workflow tracking for all cases</p>
      </div>

      {/* Status Summary */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="crm-card p-4 text-center">
          <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-2">
            <Clock className="w-5 h-5 text-slate-600" />
          </div>
          <p className="text-2xl font-bold text-slate-805">{statusCounts.created}</p>
          <p className="text-xs text-slate-500">Created</p>
        </div>
        <div className="crm-card p-4 text-center">
          <div className="w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-2 border border-amber-100">
            <GitBranch className="w-5 h-5 text-amber-600" />
          </div>
          <p className="text-2xl font-bold text-slate-805">{statusCounts.inProgress}</p>
          <p className="text-xs text-slate-500">In Progress</p>
        </div>
        <div className="crm-card p-4 text-center">
          <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center mx-auto mb-2 border border-blue-100">
            <AlertTriangle className="w-5 h-5 text-blue-600" />
          </div>
          <p className="text-2xl font-bold text-slate-805">{statusCounts.qcPending}</p>
          <p className="text-xs text-slate-500">QC Pending</p>
        </div>
        <div className="crm-card p-4 text-center">
          <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-2 border border-emerald-100">
            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
          </div>
          <p className="text-2xl font-bold text-slate-805">{statusCounts.completed}</p>
          <p className="text-xs text-slate-500">Completed</p>
        </div>
        <div className="crm-card p-4 text-center">
          <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-2 border border-red-100">
            <XCircle className="w-5 h-5 text-red-600" />
          </div>
          <p className="text-2xl font-bold text-slate-805">{statusCounts.issues}</p>
          <p className="text-xs text-slate-500">Issues</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Case List */}
        <div className="crm-card p-4">
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input
              placeholder="Search cases..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-slate-50/50 border-slate-200"
            />
          </div>
          <ScrollArea className="h-[500px]">
            <div className="space-y-2">
              {filteredCases.map((caseItem) => (
                <div
                  key={caseItem.id}
                  onClick={() => setSelectedCase(caseItem)}
                  className={`p-3 rounded-lg cursor-pointer transition-colors ${
                    selectedCase?.id === caseItem.id
                      ? 'bg-blue-50 border border-blue-500/30'
                      : 'bg-white hover:bg-slate-100/50 border border-transparent'
                  }`}
                  data-testid={`case-item-${caseItem.id}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-blue-600">{caseItem.refNumber}</span>
                    <StatusBadge
                      status={STATUS_CONFIG[caseItem.status]?.color || 'neutral'}
                      label={STATUS_CONFIG[caseItem.status]?.label || caseItem.status}
                      size="sm"
                      showIcon={false}
                    />
                  </div>
                  <p className="text-xs text-slate-500 truncate">{caseItem.applicantName}</p>
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>

        {/* Status Detail */}
        <div className="lg:col-span-2 crm-card p-6">
          {selectedCase ? (
            <div className="space-y-6">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-xl font-semibold text-slate-900">{selectedCase.refNumber}</h3>
                  <p className="text-sm text-slate-500 mt-1">
                    {selectedCase.applicantName} • {selectedCase.product.name}
                  </p>
                </div>
                <StatusBadge
                  status={STATUS_CONFIG[selectedCase.status]?.color || 'neutral'}
                  label={STATUS_CONFIG[selectedCase.status]?.label || selectedCase.status}
                />
              </div>

              {/* Client Actions */}
              {currentRole === ROLES.CLIENT && (
                <div className="flex items-center gap-3 mt-4">
                  <Button 
                    variant="outline" 
                    className="border-red-200 text-red-600 hover:bg-red-50 gap-2"
                    onClick={() => setDeleteModalOpen(true)}
                  >
                    <Trash2 className="w-4 h-4" />
                    Request Deletion
                  </Button>
                  <Button 
                    variant="outline" 
                    className="border-blue-200 text-blue-600 hover:bg-blue-50 gap-2"
                    onClick={() => {
                      setEditFormData({
                        applicantName: selectedCase.applicantName,
                        address: selectedCase.applicantAddress || '123 Main St, Mumbai',
                        remarks: ''
                      });
                      setEditFormModalOpen(true);
                    }}
                  >
                    <FileEdit className="w-4 h-4" />
                    Edit Form Data
                  </Button>
                </div>
              )}

              {/* Visual Status Tracker */}
              <div className="py-4">
                <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">Progress</h4>
                <StatusTracker
                  currentStep={getStepFromStatus(selectedCase.status)}
                  steps={statusSteps}
                />
              </div>

              {/* Case Details */}
              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-slate-200">
                <div>
                  <p className="text-xs text-slate-500">Client</p>
                  <p className="text-sm text-slate-900">{selectedCase.client.name}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Product</p>
                  <p className="text-sm text-slate-900">{selectedCase.product.name}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Created</p>
                  <p className="text-sm text-slate-900">{format(new Date(selectedCase.createdAt), 'MMM dd, yyyy')}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Due Date</p>
                  <p className="text-sm text-slate-900">{format(new Date(selectedCase.dueDate), 'MMM dd, yyyy')}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Assigned To</p>
                  <p className="text-sm text-slate-900">{selectedCase.assignedTo?.name || 'Unassigned'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Priority</p>
                  <Badge variant="outline" className={`text-xs capitalize font-semibold ${
                    selectedCase.priority === 'high' ? 'bg-red-50 text-red-700 border-red-200' :
                    selectedCase.priority === 'medium' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                    'bg-emerald-50 text-emerald-700 border-emerald-200'
                  }`}>
                    {selectedCase.priority}
                  </Badge>
                </div>
              </div>

              {/* Timeline */}
              {selectedCase.timeline.length > 0 && (
                <div className="pt-4 border-t border-slate-200">
                  <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">Activity Timeline</h4>
                  <div className="space-y-3">
                    {selectedCase.timeline.map((event, index) => (
                      <div key={index} className="flex gap-3">
                        <div className="flex flex-col items-center">
                          <div className="w-2 h-2 rounded-full bg-blue-600" />
                          {index < selectedCase.timeline.length - 1 && (
                            <div className="w-0.5 h-full bg-slate-200 my-1" />
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="text-sm text-slate-900">{event.action}</p>
                          <p className="text-xs text-slate-500">
                            {event.user.name} • {format(new Date(event.timestamp), 'MMM dd, hh:mm a')}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-slate-500">
              <GitBranch className="w-12 h-12 mb-4" />
              <p className="text-sm">Select a case to view its status</p>
            </div>
          )}
        </div>
      </div>

      {/* Delete Request Modal */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent className="bg-white text-slate-800 border-slate-200">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Request Case Deletion</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-blue-50 border border-blue-150 rounded-md">
              <p className="text-sm text-blue-700 font-medium">Deletion requests are subject to approval. The status of your request will be tracked.</p>
            </div>
            <div className="space-y-2">
              <Label>Reason for Deletion <span className="text-red-500">*</span></Label>
              <Textarea 
                placeholder="Please provide a valid reason..."
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                className="bg-white border-slate-200 min-h-[100px] text-slate-805"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteModalOpen(false)} className="border-slate-200 hover:bg-slate-50 text-slate-605">
              Cancel
            </Button>
            <Button onClick={() => {
              alert('Deletion request submitted. Status: Pending');
              setDeleteModalOpen(false);
              setDeleteReason('');
            }} disabled={!deleteReason} className="bg-red-600 hover:bg-red-700 text-white">
              Submit Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Form Modal */}
      <Dialog open={editFormModalOpen} onOpenChange={setEditFormModalOpen}>
        <DialogContent className="bg-white text-slate-800 border-slate-200">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Edit Verification Form</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Applicant Name</Label>
              <Input 
                value={editFormData.applicantName}
                onChange={(e) => setEditFormData(prev => ({...prev, applicantName: e.target.value}))}
                className="bg-white border-slate-200 text-slate-805"
              />
            </div>
            <div className="space-y-2">
              <Label>Applicant Address</Label>
              <Input 
                value={editFormData.address}
                onChange={(e) => setEditFormData(prev => ({...prev, address: e.target.value}))}
                className="bg-white border-slate-200 text-slate-805"
              />
            </div>
            <div className="space-y-2">
              <Label>Update Remarks</Label>
              <Textarea 
                placeholder="Note what changes were made..."
                value={editFormData.remarks}
                onChange={(e) => setEditFormData(prev => ({...prev, remarks: e.target.value}))}
                className="bg-white border-slate-200 text-slate-805"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditFormModalOpen(false)} className="border-slate-200 hover:bg-slate-50 text-slate-605">
              Cancel
            </Button>
            <Button onClick={() => {
              alert('Form updated successfully.');
              setEditFormModalOpen(false);
            }} className="bg-blue-600 hover:bg-blue-700 text-white">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
